/**
 * The rules behind the lot grids, kept pure so they can be tested on their own: the Secondary
 * Market list, the lot pages and the operators' Auctions lists. The player's Auctions page lists
 * auctions, not lots, since #236 (auctionList.js), and no longer uses them.
 *
 * The grid used to decide team statuses itself: rows fetched without a timer were flipped to
 * Closed while rendering, a one-second ticker spotted the flip and reloaded the whole table behind
 * a spinner, and that repeated every few seconds while an auction was live (#118). The server now
 * owns every status change - the REST page, the Worker's ReceiveBidStatus push and ReceiveCurrentBid
 * - and these functions only merge what it says. Countdowns are kept apart from the rows, as lot
 * clocks (lotClock.js) keyed by team, so replacing the rows can never reset one.
 *
 * #191: the same functions serve the Secondary Market list, whose rows are resale listings - one
 * team can have a level-1 and a level-2 listing at once - so each takes the key to match rows by
 * (keyOf: teamKey here, listingKey there). Newest wins: a pushed item produced before the row it
 * would update (serverNowUtc) is ignored.
 */

import {
    TIMER_DRIFT_MS,
    formatMmSs,
    isOlder,
    nextClock,
    parseMmSs,
    parseServerUtc,
} from './lotClock';

// Moved to lotClock.js (#191); still exported from here for the grid's existing imports.
export { TIMER_DRIFT_MS, formatMmSs, parseMmSs };

/**
 * Identifies one team in one auction. tblAuctionTeams' unique (auction, team, level) index makes
 * it unique at the grid's level 0; it is also how pushes, which carry no grid row id, are matched.
 *
 * @param {{auctionId: number, teamId: number}} row A grid row or a pushed item.
 * @returns {string} The key, e.g. "12-7".
 */
export const teamKey = (row) => `${row?.auctionId}-${row?.teamId}`;

/**
 * Identifies one resale listing (#191). A team's listings are told apart by their level, so a push
 * for its level-2 listing must not touch the level-1 row.
 *
 * @param {{auctionId: number, teamId: number, auctionLevel: number}} row A listing row or item.
 * @returns {string} The key, e.g. "12-7-1".
 */
export const listingKey = (row) => `${row?.auctionId}-${row?.teamId}-${row?.auctionLevel ?? 0}`;

/**
 * The "Start Bid" / "Last Bid" field of the lot pages (#176), decided on the figures shown. It used
 * to switch on totalPlacedBids from the router state - the row the page was opened from - while its
 * amount followed the live figures, so a lot bid on since read "Start Bid" over the top bid.
 *
 * A lot with a top bid shows it as the last bid; one without shows its starting bid. An API from
 * before topBidAmount sends none: the label then follows the figures' totalPlacedBids and the
 * amount is startingBid, as before.
 *
 * @param {object} figures The lot's figures as the page shows them.
 * @returns {{label: string, amount: *}}
 */
export const lastBidField = (figures) => {
    if (figures?.topBidAmount === undefined) {
        return {
            label: Number(figures?.totalPlacedBids) === 0 ? 'Start Bid' : 'Last Bid',
            amount: figures?.startingBid,
        };
    }
    return Number.isFinite(figures.topBidAmount)
        ? { label: 'Last Bid', amount: figures.topBidAmount }
        : { label: 'Start Bid', amount: figures.startingBid };
};

/**
 * The status to show for a row or pushed item. A team the server still calls Live with no time
 * left is shown Closed, which is what it becomes on the status job's next run.
 */
const shownStatus = (item) =>
    item?.status === 'Live' && item?.remainingBidTime === '00:00' ? 'Closed' : item?.status;

/**
 * Turns a GetBidWithStatus page into grid rows with the shown status.
 *
 * #133: the rows are no longer filtered by manager name here. The server already returns only the
 * player's managers' auctions, and the Select Manager filter is applied by the server too
 * (ManagerId) - filtering a page after the server had cut it left short pages and wrong totals.
 *
 * #178: nor is a team promoted to Live here. Between one team closing and the next starting the
 * grid used to show the first upcoming team as Live with a full timer, before the server had started
 * it - and a bid on it was refused. The server starts the next team in the same pass that closes
 * the last one and pushes both moments (ReceiveBidStatus), so an Upcoming row stays Upcoming until
 * the server says otherwise.
 *
 * @param {Array|undefined} dataList The page's rows as the server sent them.
 * @returns {Array} The rows to show.
 */
export const normalizeGridRows = (dataList) => {
    if (!Array.isArray(dataList)) return [];

    return dataList.map((row) => ({ ...row, status: shownStatus(row) }));
};

/**
 * Copies the pushed fields onto the rows they belong to. Rows that are not in the push are left
 * exactly as they are - a push covers only unfinished teams, so absence says nothing about a row.
 * #191: an item produced before the row it matches is skipped, and serverNowUtc is always copied
 * with the fields, so the row knows how new it is.
 *
 * @param {Array} rows The grid rows.
 * @param {Array} pushed The pushed items.
 * @param {string[]} fields The fields to copy; "status" is copied as the shown status.
 * @param {(row: object) => string} [keyOf] Matches items to rows: teamKey, or listingKey.
 * @returns {Array} New rows, or the same array when nothing matched.
 */
export const mergePushedRows = (rows, pushed, fields, keyOf = teamKey) => {
    if (!Array.isArray(rows) || !Array.isArray(pushed) || pushed.length === 0) return rows;

    const pushedByKey = new Map(pushed.map((item) => [keyOf(item), item]));
    let matched = false;

    const merged = rows.map((row) => {
        const item = pushedByKey.get(keyOf(row));
        if (!item || isOlder(item, parseServerUtc(row?.serverNowUtc))) return row;

        matched = true;
        const update = {};
        fields.forEach((field) => {
            if (item[field] !== undefined) update[field] = item[field];
        });
        if (fields.includes('status') && item.status !== undefined) update.status = shownStatus(item);
        if (item.serverNowUtc !== undefined) update.serverNowUtc = item.serverNowUtc;

        return { ...row, ...update };
    });

    return matched ? merged : rows;
};

/**
 * A page just fetched, keeping the pushed fields of any row a push has already brought further
 * (#191). The page was read on the server before it arrived, so a push received meanwhile - a bid,
 * a close - is newer, and replacing the rows outright would undo it until the next push.
 *
 * @param {Array} freshRows The page as it arrived, normalized.
 * @param {Array} prevRows The rows shown until now.
 * @param {string[]} fields The pushed fields to keep from a newer row.
 * @param {(row: object) => string} [keyOf] teamKey, or listingKey.
 * @returns {Array} freshRows itself, or a copy with the newer rows' fields.
 */
export const preferNewerRows = (freshRows, prevRows, fields, keyOf = teamKey) => {
    if (!Array.isArray(freshRows) || !Array.isArray(prevRows) || prevRows.length === 0) {
        return freshRows;
    }

    const freshByKey = new Map(freshRows.map((row) => [keyOf(row), row]));
    const newer = prevRows.filter((row) => {
        const fresh = freshByKey.get(keyOf(row));
        return fresh !== undefined && isOlder(fresh, parseServerUtc(row?.serverNowUtc));
    });

    return newer.length === 0 ? freshRows : mergePushedRows(freshRows, newer, fields, keyOf);
};

const sameEnds = (a, b) => {
    const aKeys = Object.keys(a);
    return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
};

/**
 * Updates the lots' clocks from data the server has just sent. #191: each entry is a nextClock()
 * object - entry.endsAt is a Live lot's deadline, entry.frozenSeconds a Paused lot's time.
 *
 * Only ever pass fresh data - a REST page as it arrived, or a push payload - never rows after a
 * merge: a row keeps whatever timer it last received, and replaying an older one would rewind a
 * countdown that has since moved on. An item older than the entry it would update is ignored.
 *
 * @param {Object<string, object>} prevEnds Clocks by key.
 * @param {Array} freshItems Rows or pushed items carrying status, remainingBidTime and, from a
 *   current API, endsAtUtc and serverNowUtc.
 * @param {number} now Date.now() at the moment the data arrived.
 * @param {{prune?: boolean, keyOf?: (row: object) => string}} [options] prune: drop the keys
 *   missing from freshItems (a full page); a key it has keeps its entry when that is newer. keyOf:
 *   teamKey, or listingKey.
 * @returns {Object<string, object>} The new clocks, or prevEnds itself when nothing changed.
 */
export const syncTimerEnds = (prevEnds, freshItems, now, { prune = false, keyOf = teamKey } = {}) => {
    const previous = prevEnds || {};
    const next = prune ? {} : { ...previous };

    (freshItems || []).forEach((item) => {
        const key = keyOf(item);
        // Read with the shown status: a Live team with no time left has no countdown either.
        next[key] = nextClock(next[key] ?? previous[key], { ...item, status: shownStatus(item) }, now);
    });

    return sameEnds(previous, next) ? previous : next;
};

/**
 * Whether a push starts or stops a live team that is on the page. That changes which teams sort
 * first and whether an auction can still be unlocked, neither of which a push carries. An item
 * older than its row says nothing (#191).
 *
 * @param {Array} rows The grid rows before the push is merged.
 * @param {Array} pushed The pushed items.
 * @param {(row: object) => string} [keyOf] teamKey, or listingKey.
 * @returns {boolean} True when a shown team's liveness differs from what the push says.
 */
export const pageLivenessChanged = (rows, pushed, keyOf = teamKey) => {
    if (!Array.isArray(rows) || !Array.isArray(pushed)) return false;

    const pushedByKey = new Map(pushed.map((item) => [keyOf(item), item]));
    return rows.some((row) => {
        const item = pushedByKey.get(keyOf(row));
        if (!item || isOlder(item, parseServerUtc(row?.serverNowUtc))) return false;
        return (shownStatus(item) === 'Live') !== (row.status === 'Live');
    });
};

/**
 * One auction's status from the rows a push carries for it, for the manager's Auctions list
 * (#191): Live while any team is live, Paused while any is paused, Upcoming while none has started.
 * Any other mix - a team closed and the next not open yet, or every team closed - is null: the push
 * cannot tell, and the list asks the server.
 *
 * @param {Array} rows The pushed rows of one auction.
 * @returns {'Live'|'Paused'|'Upcoming'|null}
 */
export const deriveAuctionStatus = (rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return null;
    if (rows.some((row) => row?.status === 'Live')) return 'Live';
    if (rows.some((row) => row?.status === 'Paused')) return 'Paused';
    if (rows.every((row) => row?.status === 'Upcoming')) return 'Upcoming';
    return null;
};
